/**
 * AircraftExpandableRow - Block D
 * 
 * Click-to-expand detail panel for Aircraft Fleet table.
 * Contains three sections:
 * - Equipment: Batteries + Accessories (EquipmentItems)
 * - Software & Firmware: firmware_version + firmware_up_to_date badge
 * - Service History: Maintenance records with Block A fields
 * 
 * Block E3a: Equipment Suggestion Banner
 * - Shows when inventory implies a SORA flag that is OFF
 * - Non-committing: nothing changes until user confirms
 * - Permission-aware: disabled for non-fleet users
 * 
 * RBAC: "Add record" button hidden for non-fleet roles (pilot)
 */

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ChevronDown, ChevronUp, Package, Cpu, History, 
  Plus, Battery, Wrench, AlertCircle, CheckCircle,
  Loader2, Info, ShieldCheck
} from 'lucide-react';
import { ServiceLogForm } from './ServiceLogForm';
import { toast } from 'sonner';

// Fleet management roles that can write maintenance records and confirm equipment
const FLEET_ROLES = ['platform_admin', 'org_admin', 'organization_admin', 'compliance_manager'];

// Status dot component - reusable
const StatusDot = ({ status }) => {
  const colorMap = {
    active: 'bg-emerald-500',
    good: 'bg-emerald-500',
    maintenance: 'bg-amber-500',
    warning: 'bg-amber-500',
    retired: 'bg-slate-400',
    replace: 'bg-slate-400'
  };
  
  return (
    <span 
      className={`inline-block w-2 h-2 rounded-full ${colorMap[status] || 'bg-slate-400'}`}
      aria-label={status}
    />
  );
};

// Equipment item row
const EquipmentItemRow = ({ item, type, t }) => {
  const getItemName = () => {
    if (type === 'battery') {
      return `${item.model || 'Battery'} (${item.serial_number})`;
    }
    // Equipment item
    const typeLabel = t.aircraftDetail?.equipmentTypes?.[item.type] || item.type;
    return `${item.model_name || typeLabel} (${item.serial_number})`;
  };

  const getStatus = () => {
    if (type === 'battery') {
      // Battery status based on cycle usage
      const usage = (item.cycle_count / item.cycle_limit) * 100;
      if (usage >= 90) return 'replace';
      if (usage >= 70) return 'warning';
      return 'good';
    }
    return item.status || 'active';
  };

  return (
    <li className="flex items-center gap-2 py-1.5 text-sm">
      <StatusDot status={getStatus()} />
      <span className="text-slate-700 dark:text-slate-300">{getItemName()}</span>
      {type === 'battery' && (
        <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
          {item.cycle_count}/{item.cycle_limit}
        </span>
      )}
    </li>
  );
};

// SORA equipment types that map to inventory
const SORA_EQUIPMENT_TYPES = {
  remote_id: ['remote_id', 'remote_id_module', 'rid_module'],
  parachute: ['parachute', 'parachute_system'],
  flight_termination_system: ['fts', 'flight_termination_system', 'flight_termination'],
  geo_awareness: ['geo_awareness', 'geo_awareness_module', 'geofence']
};

/**
 * Block E3a: Equipment Suggestion Banner
 * 
 * Shows when inventory implies a SORA flag that is currently OFF.
 * Non-committing: nothing changes until user explicitly confirms.
 * Permission-aware: disabled for non-fleet users.
 */
const EquipmentSuggestionBanner = ({ 
  aircraft, 
  assignedEquipment, 
  canConfirm, 
  onConfirm, 
  t 
}) => {
  const [confirming, setConfirming] = useState(null);
  
  // Get current aircraft equipment flags (explicit keys only - Rule: no iteration over confirmed_equipment scalars)
  const currentEquipment = aircraft.equipment || {};
  const soraFlags = {
    remote_id: currentEquipment.remote_id || false,
    parachute: currentEquipment.parachute || false,
    flight_termination_system: currentEquipment.flight_termination_system || currentEquipment.fts || false,
    geo_awareness: currentEquipment.geo_awareness || false
  };
  
  // Check which SORA flags are implied by inventory but currently OFF
  const suggestions = [];
  
  for (const [flagKey, inventoryTypes] of Object.entries(SORA_EQUIPMENT_TYPES)) {
    // Skip if flag is already ON
    if (soraFlags[flagKey]) continue;
    
    // Check if any assigned equipment matches this SORA type
    const matchingItem = assignedEquipment.find(item => 
      inventoryTypes.includes(item.type?.toLowerCase())
    );
    
    if (matchingItem) {
      suggestions.push({
        flag: flagKey,
        item: matchingItem,
        messageKey: `${flagKey.replace(/_/g, '')}Suggestion`
      });
    }
  }
  
  // No suggestions needed
  if (suggestions.length === 0) return null;
  
  const handleConfirm = async (suggestion) => {
    if (!canConfirm) return;
    
    setConfirming(suggestion.flag);
    try {
      // Build partial payload with only the changed equipment flag
      const updatedEquipment = {
        parachute: soraFlags.parachute,
        flight_termination_system: soraFlags.flight_termination_system,
        geo_awareness: soraFlags.geo_awareness,
        remote_id: soraFlags.remote_id,
        [suggestion.flag]: true // Set the confirmed flag to true
      };
      
      await api.put(`/aircraft/${aircraft.id}`, { equipment: updatedEquipment });
      toast.success(t.aircraftDetail.equipmentSuggestion.confirmed);
      onConfirm?.();
    } catch (err) {
      console.error('Failed to confirm equipment:', err);
      toast.error(
        err.response?.data?.detail || 
        t.aircraftDetail.equipmentSuggestion.confirmFailed
      );
    } finally {
      setConfirming(null);
    }
  };
  
  // Get suggestion message based on flag key
  const getSuggestionMessage = (flagKey) => {
    const messages = t.aircraftDetail?.equipmentSuggestion || {};
    switch (flagKey) {
      case 'remote_id':
        return messages.remoteIdSuggestion;
      case 'parachute':
        return messages.parachuteSuggestion;
      case 'flight_termination_system':
        return messages.ftsSuggestion;
      case 'geo_awareness':
        return messages.geoAwarenessSuggestion;
      default:
        return null;
    }
  };
  
  return (
    <div className="mb-4 space-y-2" data-testid="equipment-suggestion-banner">
      {suggestions.map(suggestion => (
        <div
          key={suggestion.flag}
          className="flex items-start gap-3 p-3 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20"
          data-testid={`equipment-suggestion-${suggestion.flag}`}
        >
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {getSuggestionMessage(suggestion.flag)}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              {suggestion.item.model_name || suggestion.item.name} ({suggestion.item.serial_number})
            </p>
          </div>
          <div className="flex-shrink-0">
            {canConfirm ? (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/40"
                onClick={() => handleConfirm(suggestion)}
                disabled={confirming === suggestion.flag}
                data-testid={`confirm-equipment-${suggestion.flag}`}
              >
                {confirming === suggestion.flag ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <ShieldCheck className="h-3 w-3" />
                )}
                {t.aircraftDetail.equipmentSuggestion.confirmEquipment}
              </Button>
            ) : (
              <Badge 
                variant="outline" 
                className="text-xs text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600"
                title={t.aircraftDetail.equipmentSuggestion.permissionRequired}
              >
                {t.aircraftDetail.equipmentSuggestion.permissionRequired}
              </Badge>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Equipment Section
const EquipmentSection = ({ aircraftId, orgId, t, language }) => {
  const [batteries, setBatteries] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const [batteriesRes, equipmentRes] = await Promise.all([
          api.get('/batteries'),
          api.get(`/equipment?lang=${language}`)
        ]);
        
        // Filter batteries assigned to this aircraft
        const assignedBatteries = batteriesRes.data.filter(
          b => b.aircraft_id === aircraftId
        );
        setBatteries(assignedBatteries);
        
        // Filter equipment assigned to this aircraft
        const assignedEquipment = equipmentRes.data.filter(
          e => e.assigned_aircraft_ids?.includes(aircraftId)
        );
        setEquipment(assignedEquipment);
      } catch (err) {
        console.error('Failed to fetch equipment:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEquipment();
  }, [aircraftId, language]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const hasEquipment = batteries.length > 0 || equipment.length > 0;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-700 dark:text-slate-300">
          <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          {t.aircraftDetail?.equipment || 'Equipment'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasEquipment ? (
          <div className="text-center py-6">
            <Package className="h-8 w-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t.aircraftDetail?.noEquipment || 'No equipment assigned'}
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              {t.aircraftDetail?.noEquipmentHint || 'Assign batteries or accessories'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {batteries.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                  {t.aircraftDetail?.batteries || 'Batteries'}
                </p>
                <ul className="space-y-0.5">
                  {batteries.map(bat => (
                    <EquipmentItemRow key={bat.id} item={bat} type="battery" t={t} />
                  ))}
                </ul>
              </div>
            )}
            {equipment.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                  {t.aircraftDetail?.accessories || 'Accessories'}
                </p>
                <ul className="space-y-0.5">
                  {equipment.map(eq => (
                    <EquipmentItemRow key={eq.id} item={eq} type="equipment" t={t} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Firmware Section
const FirmwareSection = ({ aircraft, t }) => {
  const { firmware_version, firmware_up_to_date } = aircraft;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-700 dark:text-slate-300">
          <Cpu className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          {t.aircraftDetail?.softwareFirmware || 'Software & Firmware'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
              {t.aircraftDetail?.firmwareVersion || 'Firmware Version'}
            </p>
            <p className="text-sm font-mono text-slate-700 dark:text-slate-300">
              {firmware_version || '—'}
            </p>
          </div>
          
          {/* Firmware status badge - ONLY driven by firmware_up_to_date */}
          {firmware_up_to_date === true && (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800 gap-1">
              <CheckCircle className="h-3 w-3" />
              {t.aircraftDetail?.firmwareUpToDate || 'Up to date'}
            </Badge>
          )}
          {firmware_up_to_date === false && (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 gap-1">
              <AlertCircle className="h-3 w-3" />
              {t.aircraftDetail?.firmwareUpdateAvailable || 'Update available'}
            </Badge>
          )}
          {/* null = no badge shown */}
        </div>
      </CardContent>
    </Card>
  );
};

// Service History Section
const ServiceHistorySection = ({ aircraftId, canWrite, onRefresh, t, language }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduler, setShowScheduler] = useState(false);

  const fetchRecords = useCallback(async () => {
    try {
      const res = await api.get(`/aircraft/${aircraftId}/maintenance?lang=${language}&limit=10`);
      // Sort by date descending, filter completed
      const completed = res.data
        .filter(r => r.status === 'completed')
        .sort((a, b) => new Date(b.start_at) - new Date(a.start_at));
      setRecords(completed);
    } catch (err) {
      console.error('Failed to fetch maintenance records:', err);
    } finally {
      setLoading(false);
    }
  }, [aircraftId, language]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleSchedulerClose = () => {
    setShowScheduler(false);
    fetchRecords(); // Refresh records after dialog closes
    onRefresh?.();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatParts = (parts) => {
    if (!parts || parts.length === 0) return '—';
    return parts.map(p => p.name || p.part_number).join(', ');
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-slate-700 dark:text-slate-300">
              <History className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              {t.aircraftDetail?.serviceHistory || 'Service History'}
            </CardTitle>
            {/* RBAC: Button hidden for non-fleet roles */}
            {canWrite && (
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 text-xs gap-1"
                onClick={() => setShowScheduler(true)}
                data-testid="add-service-record-btn"
              >
                <Plus className="h-3 w-3" />
                {t.aircraftDetail?.addRecord || 'Add record'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {records.length === 0 ? (
            <div className="text-center py-6">
              <Wrench className="h-8 w-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t.aircraftDetail?.noServiceRecords || 'No service records yet'}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                {t.aircraftDetail?.noServiceRecordsHint || 'Add maintenance records'}
              </p>
            </div>
          ) : (
            <ul className="space-y-3 max-h-48 overflow-y-auto">
              {records.map(record => (
                <li 
                  key={record.id} 
                  className="border-b border-slate-100 dark:border-slate-700 pb-2 last:border-0"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                        {record.work_performed || record.reason || '—'}
                      </p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {record.parts_used && record.parts_used.length > 0 && (
                          <span>
                            <span className="font-medium">{t.aircraftDetail?.partsUsed || 'Parts'}:</span>{' '}
                            {formatParts(record.parts_used)}
                          </span>
                        )}
                        {record.technician && (
                          <span>
                            <span className="font-medium">{t.aircraftDetail?.technician || 'Tech'}:</span>{' '}
                            {record.technician}
                          </span>
                        )}
                        {record.labor_hours && (
                          <span>
                            {record.labor_hours}h
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                      {formatDate(record.start_at)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Service Log Form Dialog */}
      <ServiceLogForm
        isOpen={showScheduler}
        onClose={handleSchedulerClose}
        aircraftId={aircraftId}
        onSuccess={fetchRecords}
      />
    </>
  );
};

// Main Expandable Row Component
export const AircraftExpandableRow = ({ aircraft, isOpen, onToggle, onRefresh }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [assignedEquipment, setAssignedEquipment] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(true);
  
  // RBAC check: can this user write maintenance records and confirm equipment?
  const canWriteMaintenance = FLEET_ROLES.includes(user?.role);
  const canConfirmEquipment = FLEET_ROLES.includes(user?.role);

  // Fetch assigned equipment for suggestion banner
  const fetchAssignedEquipment = useCallback(async () => {
    if (!isOpen) return;
    try {
      const res = await api.get(`/equipment?lang=${language}`);
      const assigned = res.data.filter(
        e => e.assigned_aircraft_ids?.includes(aircraft.id)
      );
      setAssignedEquipment(assigned);
    } catch (err) {
      console.error('Failed to fetch equipment for suggestions:', err);
    } finally {
      setLoadingEquipment(false);
    }
  }, [aircraft.id, isOpen, language]);

  useEffect(() => {
    fetchAssignedEquipment();
  }, [fetchAssignedEquipment]);

  const handleEquipmentConfirmed = () => {
    // Refresh parent to get updated aircraft data
    onRefresh?.();
    fetchAssignedEquipment();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="px-4 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700"
      data-testid={`aircraft-detail-panel-${aircraft.id}`}
    >
      {/* Block E3a: Equipment Suggestion Banner */}
      {!loadingEquipment && (
        <EquipmentSuggestionBanner
          aircraft={aircraft}
          assignedEquipment={assignedEquipment}
          canConfirm={canConfirmEquipment}
          onConfirm={handleEquipmentConfirmed}
          t={t}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Equipment Section */}
        <EquipmentSection 
          aircraftId={aircraft.id} 
          orgId={aircraft.organization_id}
          t={t}
          language={language}
        />
        
        {/* Firmware Section */}
        <FirmwareSection aircraft={aircraft} t={t} />
        
        {/* Service History Section */}
        <ServiceHistorySection 
          aircraftId={aircraft.id}
          canWrite={canWriteMaintenance}
          t={t}
          language={language}
        />
      </div>
    </div>
  );
};

// Toggle button for the row
export const AircraftRowToggle = ({ isOpen }) => {
  return isOpen ? (
    <ChevronUp className="h-4 w-4 text-slate-400" />
  ) : (
    <ChevronDown className="h-4 w-4 text-slate-400" />
  );
};

export default AircraftExpandableRow;
